/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

/**
 * A visible page or an ellipsis for pages omitted between visible pages.
 *
 * Each slot has a stable key based on its position in the page sequence. Gaps
 * use the first omitted page because they have no page number of their own.
 */
export type PageSlot = { key: string } & ({ kind: "page"; page: number } | { kind: "gap" })

/** Number of pages shown on either side of the current page. */
const RADIUS = 1

/**
 * Builds the page slots for the current page.
 *
 * Always includes the first and last page, plus `RADIUS` pages on either side
 * of the current page. Consecutive omitted pages are collapsed into one gap.
 *
 * @param current - Zero-based index of the current page.
 * @param total - Total number of pages.
 * @returns The slots in render order, or an empty array when there are no pages.
 *
 * @example
 * pageWindow(5, 10) // 1 … 5 6 7 … 10
 * pageWindow(0, 3)  // 1 2 3
 */
export function pageWindow(current: number, total: number): PageSlot[] {
    const slots: PageSlot[] = []

    for (let page = 0; page < total; page++) {
        if (page === 0 || page === total - 1 || Math.abs(page - current) <= RADIUS) {
            slots.push({ key: `page-${page}`, kind: "page", page })
        } else if (slots.at(-1)?.kind !== "gap") {
            slots.push({ key: `gap-${page}`, kind: "gap" })
        }
    }

    return slots
}
